import { useTranslation } from "react-i18next";
import closeicon from "../../../assets/images/close.svg";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import { formatPremiumValue } from "../../../utils";
import BackdropModal from "../../common/modal/backdropModal";

const HomeSummaryModal = ({ show, onClose, homeFormData, quoteData }) => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";

  if (!show || !homeFormData) return null;

  // Helper function to translate cover names
  const translateCoverName = (coverName) => {
    const coverTranslations = {
      "All Risk": translate("All Risk", "جميع المخاطر"),
      "VIP": translate("VIP", "تغطية كبار الشخصيات"),
      "Domestic Helper": translate("Domestic Helper", "العامل المنزلي"),
      "Terrorism": translate("Terrorism", "تغطية التخريب والإرهاب"),
      "Sabotage and Terrorism": translate("Sabotage and Terrorism", "تغطية التخريب والإرهاب"),
    };

    return coverTranslations[coverName] || coverName;
  };

  // Use quote data if available, otherwise fall back to local calculation
  const quotationNumber =
    quoteData?.quoteNumber || quoteData?.policyNumber || "H-TEMP-001";

  let premiumCalculation, premiumRates;

  if (quoteData) {
    // Use API data
    premiumCalculation = {
      basePremium: quoteData.premium,
      subtotal: quoteData.premium,
      totalPremium: quoteData.totalPremium,
    };
  }

  const finalSubtotal = premiumCalculation.basePremium;
  const finalTax = quoteData
    ? quoteData.tax
    : finalSubtotal * (premiumRates?.vatRate || 0.1);
  const finalTotal = quoteData
    ? quoteData.totalPremium
    : finalSubtotal + finalTax;

  // Helper function for RTL-aware currency formatting with 3 decimals
  const formatCurrency = (amount) => {
    const formattedAmount = formatPremiumValue(amount, "en-US", 3);
    return isRTL ? `${formattedAmount} د.ب` : `BHD ${formattedAmount}`;
  };

  return (
    <BackdropModal show={show} onClose={onClose} hideClosebtn>
      <>
        {/* Header */}
        <div className="flex justify-between items-center mb-4 -mt-6">
          <h2 className="text-[#1A2024] text-2xl not-italic font-semibold leading-6">
            {translate("Summary", "التسعيرة")}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full max767:bg-[#E5E9EB]"
          >
            <img src={closeicon} alt="Close" className="w-6 h-6" />
          </button>
        </div>
        <div className="bg-white rounded-lg max-h-[80vh] min-w-full lg:min-w-[624px] max767:max-w-full max767:min-w-full">
          {/* Summary Details */}
          <div className="rounded-lg summary-content">
            {/* Quotation Number */}
            <div className="flex items-center justify-between bg-[#E8F4D8] px-4 py-2.5 rounded-md mb-2">
              <span className="text-[#5B6871] text-xs not-italic font-medium leading-6">
                {translate("Quotation Number", "رقم التسعيرة")}
              </span>
              <span className="text-[#5B6871] text-xs not-italic font-medium leading-6">
                {quotationNumber}
              </span>
            </div>

            {/* Premium */}
            <div className="flex justify-between mb-2 px-0 py-2.5">
              <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                {translate("Premium", "المبلغ")}
              </span>
              <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                {formatCurrency(premiumCalculation.basePremium)}
              </span>
            </div>

            {/* Coverage Add-ons - Show only selected covers from cover-prices API */}
            {homeFormData?.covers?.coverPrices && (
              <>
                {homeFormData.covers.coverPrices.map((cover) => {
                  const coverKey = cover.CoverCode.toLowerCase();

                  // Check if this cover is actually selected by the user
                  const isSelected =
                    homeFormData.covers[coverKey] ||
                    // Legacy support for old keys
                    (cover.CoverCode === "CPRO21" &&
                      homeFormData.covers.allRisk) ||
                    (cover.CoverCode === "CPRO13" &&
                      homeFormData.covers.vipCover) ||
                    (cover.CoverCode === "CPRO35" &&
                      homeFormData.covers.domesticHelper) ||
                    (cover.CoverCode === "CPRO33" &&
                      homeFormData.covers.sabotageAndTerrorism);

                  // Only show selected covers (not basic covers)
                  if (isSelected) {
                    // Get the price from cover-prices API using switch case
                    const getCoverPriceByCode = (coverCode) => {
                      switch (coverCode) {
                        case "CPRO21": // All Risk
                        case "CPRO13": // VIP Cover
                        case "CPRO35": // Domestic Helper
                        case "CPRO33": // Terrorism
                          return cover.Price || 0;
                        default:
                          // For any other cover codes from cover-prices API
                          return cover.Price || 0;
                      }
                    };

                    const coverPrice = getCoverPriceByCode(cover.CoverCode);

                    console.log(
                      `💰 Home Summary Selected Cover ${cover.CoverCode}: Cover-Prices API Price=${coverPrice}`
                    );

                    return (
                      <div
                        key={cover.CoverCode}
                        className="flex justify-between mb-2 px-0 py-2.5 border-t-[#DDE2E4] border-t border-solid"
                      >
                        <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                          {translateCoverName(cover.CoverName)}
                        </span>
                        <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                          {formatCurrency(coverPrice)}
                        </span>
                      </div>
                    );
                  }
                  return null;
                })}
              </>
            )}

            {/* VAT */}
            <div className="flex justify-between mb-2 px-0 py-2.5 border-t-[#DDE2E4] border-t border-solid">
              <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                {translate("VAT", "ضريبة القيمة المضافة")} 10%
              </span>
              <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                {formatCurrency(finalTax)}
              </span>
            </div>

            {/* Total */}
            <div className="flex justify-between px-0 py-2.5 border-t-[#DDE2E4] border-t border-solid">
              <span className="text-[#1A2024] text-base not-italic font-extrabold leading-6">
                {translate("Total", "المجموع")}
              </span>
              <span className="text-[#1A2024] text-base not-italic font-extrabold leading-6">
                {formatCurrency(finalTotal)}
              </span>
            </div>
          </div>
        </div>
      </>
    </BackdropModal>
  );
};

export default HomeSummaryModal;
